import { $, _ } from "js/vendor";
import moment from "moment";
import { app } from "js/namespaces";
import { SideBar } from "js/editor/ui";
import DummyAdapter from "js/core/storage/dummyAdapter";
import Api from "js/core/api";
import { UndoType } from "common/constants";
import getUserProfile from "js/core/services/userProfiles";
import { ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { slideTemplates } from "js/core/slide_templates/slide_template";

const calendarConfig = {
    sameDay: "[Today]",
    lastDay: "[Yesterday]",
    lastWeek: "[Last] dddd",
    sameElse: "MM/DD/YYYY"
};

const SlideRevisionsDialog = SideBar.extend({
    className: "editor_menu slide_revisions",
    type: "ShowSlideRevisionsDialog",
    lockSlide: true,
    confirmBeforeClose: true,

    initialize(options) {
        this.presentation = options.presentation;
        this.currentSlide = options.slide;
        this.isReadOnly = options.isReadOnly;
        this.slideAdapter = this.currentSlide.adapter;
        this.currentSlide.adapter = new DummyAdapter(this.currentSlide.getDummyAdapterOptions());
        this.currentUser = app.user.getAuthUser();
        this.userCache = {};
    },

    render: function() {
        this.$el.empty();
        this.$el.spinner(true);
        this.$el.addEl($.div("title", "Slide History"));
        const $revisions = this.$el.addEl($.div("commands-container"));

        const slideId = this.currentSlide.id;
        Api.slideRevisions.get({ id: slideId, page: 1 })
            .then(slideRevisions => {
                if (slideRevisions.length > 1) {
                    let prevDate = "";
                    let $revisionGroup;

                    const createRevisionGroup = displayTime => {
                        $revisionGroup = $.div("revision-group");
                        $revisionGroup.addEl($.div("revision-date", displayTime));
                        $revisions.append($revisionGroup);
                    };

                    slideRevisions.forEach((slideRevision, index) => {
                        const { revisionTimestamp, revisionCreatedBy } = slideRevision;

                        let time = moment(revisionTimestamp).format("LTS");
                        let displayTime = moment(revisionTimestamp).calendar(null, calendarConfig);

                        if (slideRevision.revisionIsInitialState) {
                            createRevisionGroup("");
                            time = "Initial State";
                            displayTime = "";
                        } else {
                            const date = moment(revisionTimestamp).format("L");
                            if (date !== prevDate) {
                                prevDate = date;
                                createRevisionGroup(displayTime);
                            }
                        }

                        // Cleanup slide revision and compose state by merging
                        // current slide attributes with revision
                        delete slideRevision.slideId;
                        delete slideRevision.revisionTimestamp;
                        delete slideRevision.revisionCreatedBy;
                        delete slideRevision.revisionIsInitialState;
                        delete slideRevision.createdAt;
                        delete slideRevision.modifiedAt;
                        const state = {
                            ...this.currentSlide.attributes,
                            ...slideRevision,
                            id: slideId
                        };

                        const $row = this.createRevisionView({
                            userId: revisionCreatedBy,
                            state,
                            time,
                            isLastRevision: index === 0
                        });
                        $revisionGroup.append($row);

                        const currentTemplate = slideRevision.template_id;
                        const nextTemplate = slideRevisions[index + 1]?.template_id;
                        const convertedTo = nextTemplate && nextTemplate !== currentTemplate ? currentTemplate : null;

                        if (convertedTo) {
                            $revisionGroup.append($.div("changed-template", `Converted to ${slideTemplates[convertedTo].title}`));
                        }
                    });
                } else {
                    const $empty = this.$el.addEl($.div("empty-container"));
                    $empty.addEl($.div("label empty", "This slide does not have any previous versions yet."));
                }

                this.$el.spinner(false);
            });

        return this;
    },

    createRevisionView({ userId, state, time, isLastRevision }) {
        const $row = $.div("revision-row");
        if (userId) {
            const $user = $row.addEl($.div("revision-user"));
            this.renderUser(userId, $user);

            if (time) {
                $row.addEl($.div("revision-time", time));
            }
        } else if (time) {
            $row.addEl($.div("revision-time-no-user", time));
        }
        if (isLastRevision) {
            this.selectedRow = $row;
            this.originalState = state;
            this.selectedState = state;
            $row.addClass("selected");
            $row.addEl($.div("current-version", "Current version"));
        }

        $row.on("click", () => this.onSelectRevision(state, $row));

        return $row;
    },

    onSelectRevision(state, $row) {
        if (this.isReadOnly) {
            return;
        }
        if (this.selectedRow === $row) return;

        this.selectedRow.removeClass("selected");
        this.selectedRow = $row;
        this.selectedState = state;
        $row.addClass("selected");

        let currentTemplate = this.currentSlide.get("template_id");
        this.currentSlide.update(state, { removeMissingKeys: true, save: false });
        this._refreshSlide(state, currentTemplate);
    },

    renderUser(userId, $user) {
        getUserProfile(userId).then(user => {
            if (user.photoURL) {
                $user.addEl($.img(user.photoURL, "revision-user-photo"));
            } else {
                $user.addEl($.icon("person").addClass("micon revision-user-photo"));
            }
            $user.addEl($.div("revision-user-name", user.displayName || user.email));
        });
    },

    showConfirmationDialog() {
        return new Promise(resolve => {
            if (this.originalState !== this.selectedState) {
                ShowConfirmationDialog({
                    title: "Are you sure you want to save the slide at this revision?",
                    acceptCallback: () => {
                        this._restoreInitialState();
                        const options = { removeMissingKeys: true };
                        const { hasUpdates } = this.currentSlide.update(this.selectedState, options);
                        if (hasUpdates) {
                            this.currentSlide.hasChanges = true;
                            if (app.undoManager) {
                                app.undoManager.set(UndoType.SLIDE_DATA, this.currentSlide.id, this.originalState, this.selectedState
                                );
                            }
                        }
                        app.mainView.editorView.renderPreventSlideEdit();
                        resolve();
                    },
                    cancelCallback: () => {
                        const currentTemplate = this.currentSlide.get("template_id");
                        const options = { removeMissingKeys: true, silent: false, save: false };
                        this._restoreInitialState(options);
                        this._refreshSlide(this.originalState, currentTemplate);
                        resolve();
                    }
                });
            } else {
                this.currentSlide.adapter = this.slideAdapter;
                resolve();
            }
        });
    },

    _restoreInitialState(paramOptions) {
        const options = paramOptions ? paramOptions : { removeMissingKeys: true, silent: true, save: false };
        this.currentSlide.update(this.originalState, options);
        this.currentSlide.adapter = this.slideAdapter;
    },

    _refreshSlide(state, currentTemplate) {
        // Special case updating for a template
        if (app.currentCanvas && state.template_id !== currentTemplate) {
            app.currentCanvas.model.template_id = state.template_id;
            app.mainView.editorView.updateCurrentCanvasUI();
        }
    }
});

export { SlideRevisionsDialog };
